import { ImageParameters, HistoryEntry } from '@/lib/mcp/types';
// import { ClientOptions, ContextData, Message as MCPMessage } from '@mcp/core'; // Auskommentiert, da Paket fehlt
// import getConfig from 'next/config';
// import { UnifiedCompletionParams, UnifiedCompletionResponse } from '@/lib/mcp/types';
// import { McpStatusListener } from './mcpStatusListener';

// Determine which service to use based on environment variable
// const IMAGE_SERVICE = process.env.NEXT_PUBLIC_IMAGE_SERVICE || 'mcp'; // Force API usage
// const IMAGE_SERVICE = 'api'; // Force API
const API_SERVER = process.env.NEXT_PUBLIC_API_SERVER || 'http://localhost:8000';

// Select the appropriate server based on the configured service
// const SERVER_URL = IMAGE_SERVICE === 'api' ? API_SERVER : MCP_SERVER; // Force API usage
// const SERVER_URL = API_SERVER; // Force API server

// Map endpoint names between MCP and API
const ENDPOINT_MAP = {
  // mcp: { ... }, // No longer needed if forcing API
  api: {
    getParameters: (imageName: string) => `parameters/${imageName}`,
    getImageHistory: (imageName: string) => `image-history/${imageName}`,
    getPromptTemplates: 'umynd-prompts',
    adaptPromptOnly: 'adapt-prompt',
    generateUmyndHero: 'generate-umynd-hero',
    generateUmyndImage: 'generate-umynd-image',
    generateImagesGemini: 'generate-images-gemini', // Assuming this exists or will exist
    restoreVersion: 'restore-version',
    colorTransfer: 'adept-image',
    imageToImage: 'image-to-image', // Assuming this exists or will exist
    analyzeImage: 'analyze-image', // Added based on API definition
    upscaleImage: 'upscale', // Added based on API definition
    flashGenerate: 'flash-generate' // Added based on API definition
  }
};

// Helper to get the correct endpoint
function getEndpoint(key: keyof typeof ENDPOINT_MAP.api, imageName?: string): string {
  // const endpointConfig = ENDPOINT_MAP[IMAGE_SERVICE as 'mcp' | 'api'][key]; // Force API
  const endpointConfig = ENDPOINT_MAP.api[key]; // Directly use API map
  if (typeof endpointConfig === 'function' && imageName !== undefined) {
    return endpointConfig(imageName);
  }
  return endpointConfig as string;
}

export async function getParameters(imageName: string): Promise<ImageParameters> {
  console.log(`Fetching parameters for ${imageName} from ${API_SERVER} (Forced API)`);
  
  try {
    const endpoint = getEndpoint('getParameters', imageName);
    const response = await fetch(`${API_SERVER}/${endpoint}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to get parameters for ${imageName}:`, errorText);
      throw new Error(`Failed to get parameters for ${imageName}: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully fetched parameters for ${imageName}:`, data);
    return data;
  } catch (error) {
    console.error(`Error fetching parameters for ${imageName}:`, error);
    // Return fallback parameters object
    return {
      type: 'image',
      width: 800,
      height: 600,
      style: 'default',
      theme: 'light',
      prompt: `Image of ${imageName}`
    };
  }
}

export async function getImageHistory(imageName: string): Promise<HistoryEntry[]> {
  console.log(`Fetching history for ${imageName} from ${API_SERVER} (Forced API)`);
  
  try {
    const endpoint = getEndpoint('getImageHistory', imageName);
    const response = await fetch(`${API_SERVER}/${endpoint}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to get history for ${imageName}:`, errorText);
      throw new Error(`Failed to get history for ${imageName}: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully fetched history for ${imageName}:`, data);
    return data;
  } catch (error) {
    console.error(`Error fetching history for ${imageName}:`, error);
    // Return empty array as fallback
    return [];
  }
}

export async function getPromptTemplates(): Promise<Record<string, string>> {
  // Use configured API_SERVER directly
  const API_PROMPT_URL = `${API_SERVER}/umynd-prompts`; 
  console.log(`Fetching prompt templates from ${API_PROMPT_URL}`);
  
  try {
    const response = await fetch(API_PROMPT_URL); 

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to get prompt templates:`, errorText);
      throw new Error(`Failed to get prompt templates: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully fetched prompt templates:`, data);
    return data;
  } catch (error) {
    console.error(`Error fetching prompt templates from ${API_PROMPT_URL}:`, error);
    
    // Check if it's a network error (fetch likely failed)
    if (error instanceof TypeError && error.message.includes('fetch failed')) { // More specific check for fetch failure
        console.error(`
❗️❗️❗️ Failed to connect to the API server at ${API_PROMPT_URL}.`);
        console.error(`Is the backend API running? If not, try starting it using:`);
        console.error(`   bash cursor_tools/start-imagen-api.sh
`);
    }
    
    // Return empty object or re-throw, depending on desired behavior. Currently returns empty.
    return {}; 
  }
}

export async function adaptPromptOnly(
  content: string,
  basePrompt: string,
  adaptSlightly: boolean = true,
  useColorPalette: boolean = false // This param seems unused by backend?
): Promise<string> {
  console.log(`Adapting prompt using Forced API to ${API_SERVER}`); 
  
  try {
    const endpoint = getEndpoint('adaptPromptOnly');
    
    const payload = {
      content,
      base_prompt: basePrompt, 
      adapt_slightly: adaptSlightly, 
      use_color_palette: useColorPalette 
    };
        
    const response = await fetch(`${API_SERVER}/${endpoint}`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to adapt prompt:`, errorText);
      throw new Error(`Failed to adapt prompt: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully adapted prompt:`, data);
    return data.adaptedPrompt || ""; // API returns adaptedPrompt 
  } catch (error) {
    console.error(`Error adapting prompt:`, error);
    return basePrompt; // Return original prompt as fallback
  }
}

// Define specific types for generateImage parameters based on API models
type ApiHeroParams = {
  image_name: string;
  prompt: string;
  content_to_adapt?: string;
  preferred_color?: string;
  adapt_slightly?: boolean;
  do_front?: boolean;
  use_custom_prompt?: boolean;
  parameters?: Record<string, unknown>;
};

type ApiImageParams = {
  image_name: string;
  prompt: string;
  content_to_adapt?: string;
  preferred_color?: string;
  adapt_slightly?: boolean;
  do_some?: boolean;
  do_front?: boolean;
  use_custom_prompt?: boolean;
  parameters?: Record<string, unknown>;
};

// Updated generateImage function
export async function generateImage(
  endpointKey: 'generateUmyndHero' | 'generateUmyndImage',
  imageName: string,
  parameters: ImageParameters
): Promise<{ image_path: string; parameters: unknown }> {
  console.log(`Attempting generateImage for OUTPUT: ${imageName} using endpoint ${endpointKey} (Forced API)`);
  console.log('Received parameters:', parameters);
  
  try {
    const endpoint = getEndpoint(endpointKey);
    
    const useCustomPrompt = parameters.use_custom_prompt === true && !!parameters.custom_prompt;
    let promptToSend = '';

    if (useCustomPrompt) {
      // Ensure custom_prompt is a string or fallback to empty string
      promptToSend = typeof parameters.custom_prompt === 'string' ? parameters.custom_prompt : '';
      console.log(`Using custom prompt for ${imageName}`);
    } else if (parameters.prompt) {
      promptToSend = parameters.prompt;
      console.log(`Using direct prompt property for ${imageName}`);
    } else {
       console.warn(`No prompt found for ${imageName}, using a placeholder.`);
       promptToSend = `Minimalist 3D render for ${imageName}`; 
    }
    
    // Construct the payload matching the specific API endpoint's Pydantic model
    let payload: ApiHeroParams | ApiImageParams;

    if (endpointKey === 'generateUmyndHero') {
      payload = {
        image_name: imageName,
        prompt: promptToSend,
        content_to_adapt: parameters.content_to_adapt || undefined,
        preferred_color: parameters.preferred_color || undefined,
        adapt_slightly: parameters.adapt_slightly !== undefined ? parameters.adapt_slightly : true,
        do_front: parameters.do_front || false,
        use_custom_prompt: useCustomPrompt,
        parameters: {
           content_to_adapt: parameters.content_to_adapt,
           preferred_color: parameters.preferred_color,
           adapt_slightly: parameters.adapt_slightly,
           do_front: parameters.do_front,
           prompt_used: promptToSend,
        }
      };
    } else { // generateUmyndImage
       payload = {
        image_name: imageName,
        prompt: promptToSend,
        content_to_adapt: parameters.content_to_adapt || undefined,
        preferred_color: parameters.preferred_color || undefined,
        adapt_slightly: parameters.adapt_slightly !== undefined ? parameters.adapt_slightly : true,
        do_some: parameters.do_some || false,
        do_front: parameters.do_front || false,
        use_custom_prompt: useCustomPrompt,
        parameters: {
           content_to_adapt: parameters.content_to_adapt,
           preferred_color: parameters.preferred_color,
           adapt_slightly: parameters.adapt_slightly,
           do_some: parameters.do_some,
           do_front: parameters.do_front,
           prompt_used: promptToSend,
        }
      };
    }

    // Remove undefined optional fields before sending
    Object.keys(payload).forEach((key) => payload[key as keyof typeof payload] === undefined && delete payload[key as keyof typeof payload]);
    if (payload.parameters) {
         Object.keys(payload.parameters).forEach(key => payload.parameters![key] === undefined && delete payload.parameters![key]);
    }
    
    console.log(`Sending Request to ${API_SERVER}/${endpoint}:`, JSON.stringify(payload, null, 2));
    
    const response = await fetch(`${API_SERVER}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      let errorText = 'Unknown error';
      try {
         errorText = await response.text();
      } catch {
        // Ignore if response body is not JSON
      }
      console.error(`Failed to generate image ${imageName}. Status: ${response.status}. Body: ${errorText}`);
      throw new Error(`Failed to generate image ${imageName}: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully received generation response for ${imageName}:`, data);
    if (!data.image_path || !data.parameters) {
        throw new Error('API response missing expected fields image_path or parameters');
    }
    return data; 
  } catch (error) {
    console.error(`Error in generateImage function for ${imageName}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

export async function restoreVersion(
  imageName: string,
  versionTimestamp: string
): Promise<{ imagePath: string; parameters: unknown; imageData: string }> {
  console.log(`Restoring version ${versionTimestamp} for image ${imageName} (Forced API)`);
  
  try {
    const endpoint = getEndpoint('restoreVersion');
    
    const payload = {
          imageName,
          versionTimestamp
        };
    
    console.log('Request payload:', payload);
    
    const response = await fetch(`${API_SERVER}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to restore version for ${imageName}:`, errorText);
      throw new Error(`Failed to restore version for ${imageName}: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully restored version for ${imageName}:`, data);
    // Ensure expected fields are present
    if (!data.imagePath || !data.parameters || !data.imageData) {
        throw new Error('API response missing expected fields imagePath, parameters, or imageData');
    }
    return data;
  } catch (error) {
    console.error(`Error restoring version for ${imageName}:`, error);
    throw error;
  }
} 

// TODO: Add functions for colorTransfer, imageToImage, analyzeImage, upscaleImage, flashGenerate
// similar to the above, using getEndpoint() and matching the API payload/response.

// Helper function to find prompt key (needs promptTemplates) - might be better to pass the key from the hook
/*
function getKeyByValue(value: string, obj: Record<string, string> | undefined): string | undefined {
  if (!obj) return undefined;
  return Object.keys(obj).find(key => obj[key] === value);
}
*/

export async function deleteHistoryVersion(imageName: string, versionTimestamp: string): Promise<unknown> {
  console.log(`Attempting to delete history version ${versionTimestamp} for ${imageName} via API`);
  
  try {
    // No specific endpoint key in ENDPOINT_MAP needed for this structure
    const endpoint = `archive/${imageName}/${versionTimestamp}`;
    const response = await fetch(`${API_SERVER}/${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      let errorDetail = `HTTP error ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || JSON.stringify(errorData);
      } catch {
        // Ignore if response body is not JSON
      }
      console.error(`Failed to delete history version ${versionTimestamp} for ${imageName}:`, errorDetail);
      throw new Error(`Failed to delete history version: ${errorDetail}`);
    }
    
    const data = await response.json();
    console.log(`Successfully deleted history version ${versionTimestamp} for ${imageName}:`, data);
    return data; // Contains status and deleted files
  } catch (error) {
    console.error(`Error deleting history version ${versionTimestamp} for ${imageName}:`, error);
    throw error; // Re-throw the error to be caught by the caller
  }
} 

// Function to connect to the MCP WebSocket server
// Erfordert MCPMessage Typ, daher auskommentiert, bis @mcp/core verfügbar ist
/*
export function connectMCPSocket(
  onMessage: (data: ContextData) => void,
  options?: Partial<ClientOptions>
): WebSocket {
  if (!mcpServerUrl) {
    throw new Error("MCP_SERVER URL is not defined in environment variables.");
  }
  
  const ws = new WebSocket(mcpServerUrl);

  ws.on('open', () => {
    console.log('Connected to MCP WebSocket server');
    // Send initial context request or other setup messages if needed
    if (options?.contextId) {
      const requestMessage: MCPMessage = {
        type: 'context_request',
        payload: { context_id: options.contextId }
      };
      ws.send(JSON.stringify(requestMessage));
    }
  });

  ws.on('message', (data: Buffer) => {
    try {
      const message: MCPMessage = JSON.parse(data.toString());
      if (message.type === 'context_update') {
        onMessage(message.payload);
      }
      // Handle other message types if necessary
    } catch (e) {
      const rawData = data instanceof Buffer ? data.toString() : String(data);
      console.error('Error parsing JSON from socket. Received data:', rawData, 'Error:', e);
    }
  });

  ws.on('close', () => {
    console.log('Disconnected from MCP WebSocket server');
  });

  ws.on('error', (error) => {
    console.error('MCP WebSocket error:', error);
  });

  return ws;
}
*/

/* Comment out misplaced methods causing parsing error
private async handleWebSocketMessage(data: Buffer | string): Promise<void> {
  // ... existing code ...
}

private async handleWebSocketClose(code: number, reason: Buffer): Promise<void> {
  // ... existing code ...
}
*/

// New WebSocket class
/* Comment out incomplete WebSocketClient class
class WebSocketClient {
  private socket: WebSocket | null = null;
  private status: string = 'disconnected';
  private details: string = '';
  private statusListeners = new Set<McpStatusListener>(); // This line will cause error if McpStatusListener is not defined/imported

  constructor(private onMessage: (data: ContextData) => void) {} // This line will cause error if ContextData is not defined/imported

  public async connect(): Promise<void> {
    if (!MCP_SERVER) {
      throw new Error("MCP_SERVER URL is not defined in environment variables.");
    }
    
    this.socket = new WebSocket(MCP_SERVER);

    this.socket.onmessage = (event) => {
      this.handleWebSocketMessage(event.data);
    };

    this.socket.onclose = (event) => {
      this.handleWebSocketClose(event.code, event.reason);
    };

    this.socket.onerror = (event) => {
      console.error('WebSocket error:', event);
      this.status = 'error';
      this.details = 'WebSocket connection error'; 
      this.notifyStatusListeners();
      this.socket?.close();
    };

    return new Promise((resolve) => {
      if (this.socket) { 
        this.socket.onopen = () => { 
          console.log('WebSocket connected');
          this.status = 'connected';
          this.notifyStatusListeners();
          resolve();
        };
      }
    });
  }

  private handleWebSocketMessage(data: Buffer | string | Blob | ArrayBuffer): void {
    // ... implementation ...
  }

  private handleWebSocketClose(code: number, reason: string): void {
    // ... implementation ...
  }


  public disconnect(): void {
    // ... implementation ...
  }

  public addStatusListener(listener: McpStatusListener): void { // This line will cause error
    this.statusListeners.add(listener);
  }

  public removeStatusListener(listener: McpStatusListener): void { // This line will cause error
    this.statusListeners.delete(listener);
  }

  private notifyStatusListeners(): void {
    // ... implementation ...
  }
}
*/